// Alerts for a restaurant's staff, on every screen: guests' requests and kitchen
// tickets ready to collect. One query feeds the bell, the count on "Tables", the
// pop-up, the chime, the tab title and the app icon's badge. They work only
// while the app is open and online on this device.
import { requestExpired } from '@ims/core'
import type { GuestRequest, KitchenTicket } from '@ims/data'
import { Badge, Button, Modal, Switch, cx, useToast } from '@ims/ui'
import { Bell, BellRing, ChefHat, Clock } from '@ims/ui/icons'
import {
  createContext,
  useContext,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { Link, useLocation } from 'react-router'
import { useLive, useNow, useSettings } from '../hooks.ts'
import {
  alertLevel,
  alertTitle,
  dismissReady,
  firstSeenAt,
  readyIsFresh,
  waitedLabel,
  type AlertLevel,
  setSound,
  soundOn,
  useAlertSound,
  useDismissedReady,
} from '../lib/alerts.ts'
import { STATION_LABEL, openFor, orderName } from '../lib/orders.ts'
import { GuestRequests } from '../screens/guest-requests.tsx'
import { ServerAvatar, useServerName } from '../screens/server-dialog.tsx'
import { PushSwitch } from './push-switch.tsx'
import { useSession, useUser } from '../session.tsx'
import { useCan } from '../workspace.tsx'

interface Alerts {
  /** False in a shop, and for a viewer: no bell at all. */
  enabled: boolean
  requests: GuestRequest[]
  ready: KitchenTicket[]
  /** Who hears each alert now, by request or ticket id (`alertLevel`). */
  levels: ReadonlyMap<string, AlertInfo>
  /** How long a call or the bill, and food ready, wait for their server (ms). */
  waits: { call: number; ready: number }
  count: number
  open: boolean
  setOpen: (open: boolean) => void
}

/**
 * An alert's level, its table's server (null: a free table), how long this device has seen
 * it (the wait for its server counts from then) and how old it is (for the words).
 */
export interface AlertInfo {
  level: AlertLevel
  serverId: string | null
  waitedMs: number
  ageMs: number
}

const NONE: Alerts = {
  enabled: false,
  requests: [],
  ready: [],
  levels: new Map(),
  waits: { call: 120_000, ready: 60_000 },
  count: 0,
  open: false,
  setOpen: () => {},
}
const AlertsContext = createContext<Alerts>(NONE)

export const useAlerts = () => useContext(AlertsContext)

function requestLabel(g: GuestRequest): string {
  const where = g.tableName ?? 'A guest'
  if (g.request.kind === 'call') return `${where} wants a waiter`
  if (g.request.kind === 'bill') return `${where} asked for the bill`
  return `${where}: order from the table`
}
const ASKING = {
  call: 'calling the waiter',
  bill: 'asking for the bill',
  order: 'order from the table',
}
/** The ticket's table, or a takeaway's number and name. */
const ticketWhere = (t: KitchenTicket) =>
  t.order ? orderName(t.order, t.tableName) : (t.tableName ?? 'No table')
const readyLabel = (t: KitchenTicket) => `#${t.ticket.kot_no} · ${ticketWhere(t)} is ready`

export function AlertsProvider({ children }: { children: ReactNode }) {
  const settings = useSettings()
  const canServe = useCan('waiter')
  const enabled = Boolean(settings?.restaurant) && canServe
  const { platform } = useSession()
  const me = useUser().id
  const serverName = useServerName()
  const toast = useToast()
  const location = useLocation()
  const now = useNow()
  const dismissed = useDismissedReady()
  const [open, setOpen] = useState(false)

  const data = useLive(
    async (r) =>
      enabled
        ? { requests: await r.orders.requests(), ready: await r.orders.ready() }
        : { requests: [], ready: [] },
    [enabled],
    [
      'order_requests',
      'order_request_items',
      'kot_tickets',
      'kot_items',
      'orders',
      'dining_tables',
    ],
  ).data

  // Someone else's alert waits for its server before everyone hears it: a quicker tick
  // than the half-minute clock while one is waiting, so it is told on time.
  const [tick, setTick] = useState(() => Date.now())
  const nowMs = Math.max(now.getTime(), tick)
  const requests = useMemo(
    () => (data?.requests ?? []).filter((g) => !requestExpired(g.request, nowMs)),
    [data, nowMs],
  )
  const ready = useMemo(
    () =>
      (data?.ready ?? []).filter(
        (t) => readyIsFresh(t.ticket.ready_at, nowMs) && !dismissed.includes(t.ticket.id),
      ),
    [data, nowMs, dismissed],
  )
  const count = requests.length + ready.length
  const waits = useMemo(
    () => ({
      call: (settings?.alert_wait_call_seconds ?? 120) * 1000,
      ready: (settings?.alert_wait_ready_seconds ?? 60) * 1000,
    }),
    [settings?.alert_wait_call_seconds, settings?.alert_wait_ready_seconds],
  )
  const levels = useMemo(() => {
    const out = new Map<string, AlertInfo>()
    const add = (id: string, serverId: string | null, waitMs: number, since: string | null) => {
      const firstSeenMs = firstSeenAt(id, nowMs)
      const waitedMs = nowMs - firstSeenMs
      out.set(id, {
        level: alertLevel({ serverId, me, firstSeenMs, nowMs, waitMs }),
        serverId,
        waitedMs,
        // What the row says ("12m ago"): never less than this device has seen it.
        ageMs: Math.max(waitedMs, since ? nowMs - Date.parse(since) : 0),
      })
    }
    for (const g of requests) add(g.request.id, g.serverId, waits.call, g.request.created_at)
    for (const t of ready)
      add(t.ticket.id, t.order?.server_id ?? null, waits.ready, t.ticket.ready_at)
    return out
  }, [requests, ready, me, nowMs, waits])
  const waiting = [...levels.values()].some((l) => l.level === 'quiet')
  useEffect(() => {
    if (!waiting) return
    const timer = setInterval(() => setTick(Date.now()), 10_000)
    return () => clearInterval(timer)
  }, [waiting])

  // An alert pops up (with the chime) when it first reaches this person: at once for their
  // own table or a free table, after its wait for someone else's. What is already waiting
  // when the app opens alerts nobody. The kitchen's own screen already says a ticket is
  // ready (with Undo), so it is not told twice.
  const told = useRef<Set<string> | null>(null)
  const announce = useEffectEvent((lines: { title: string; detail?: string; late: boolean }[]) => {
    const one = lines.length === 1 ? lines[0]! : null
    const late = lines.some((l) => l.late)
    const title = one ? one.title : `${lines.length} new alerts`
    const options = { detail: one?.detail, action: { label: 'View', onClick: () => setOpen(true) } }
    if (late) toast.warning(title, options)
    else toast.info(title, options)
    if (soundOn()) platform.alerts.chime()
  })
  const onKitchen = location.pathname.startsWith('/kitchen')
  useEffect(() => {
    if (!data) return
    const lines = new Map<string, { title: string; detail?: string; late: boolean }>()
    for (const g of requests) {
      const info = levels.get(g.request.id)
      if (!info || info.level === 'quiet') continue
      const where = g.tableName ?? 'A guest'
      const late = info.level === 'all' && info.serverId !== null
      lines.set(
        g.request.id,
        late
          ? {
              title: `${where} has waited ${waitedLabel(info.ageMs)}`,
              detail: `${serverName(info.serverId) ?? 'Another server'}’s table · ${ASKING[g.request.kind]}`,
              late,
            }
          : {
              title: requestLabel(g),
              detail: info.level === 'mine' ? 'Your table' : undefined,
              late,
            },
      )
    }
    for (const t of ready) {
      const info = levels.get(t.ticket.id)
      if (!info || info.level === 'quiet' || onKitchen) continue
      const where = ticketWhere(t)
      const late = info.level === 'all' && info.serverId !== null
      lines.set(
        t.ticket.id,
        late
          ? {
              title: `Food for ${where} is getting cold`,
              detail: `${serverName(info.serverId) ?? 'Another server'}’s table · ready ${waitedLabel(info.ageMs)} ago`,
              late,
            }
          : info.level === 'mine'
            ? {
                title: `Food ready for ${where}`,
                detail: `#${t.ticket.kot_no} ${STATION_LABEL[t.ticket.station]} · your table`,
                late,
              }
            : { title: readyLabel(t), late },
      )
    }
    if (told.current === null) {
      told.current = new Set(lines.keys())
      return
    }
    const fresh = [...lines.keys()].filter((id) => !told.current!.has(id))
    for (const id of fresh) told.current.add(id)
    if (fresh.length > 0) announce(fresh.map((id) => lines.get(id)!))
  }, [data, requests, ready, levels, onKitchen, serverName])

  const setBadge = useEffectEvent((n: number) => platform.alerts.setBadge(n))
  useEffect(() => {
    document.title = alertTitle(count, document.title)
    setBadge(count)
  }, [count])

  // A link inside the panel leaves it.
  const path = location.pathname
  const [openedOn, setOpenedOn] = useState(path)
  const shown = open && openedOn === path
  const value = useMemo<Alerts>(
    () => ({
      enabled,
      requests,
      ready,
      levels,
      waits,
      count,
      open: shown,
      setOpen: (next) => {
        setOpenedOn(path)
        setOpen(next)
      },
    }),
    [enabled, requests, ready, levels, waits, count, shown, path],
  )
  return <AlertsContext.Provider value={value}>{children}</AlertsContext.Provider>
}

/** A small count on a nav item or the bell. */
export function CountPill({ count, className }: { count: number; className?: string }) {
  if (count <= 0) return null
  return (
    <span
      aria-hidden
      className={cx(
        'inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-danger px-1 text-[11px] font-bold leading-none text-on-fill',
        className,
      )}
    >
      {count > 9 ? '9+' : count}
    </span>
  )
}

/** The bell in the top bar and its panel. Renders nothing in a shop. */
export function AlertsBell() {
  const { enabled, requests, ready, levels, waits, count, open, setOpen } = useAlerts()
  const settings = useSettings()
  const now = useNow()
  const sound = useAlertSound()
  const serverName = useServerName()
  const { platform } = useSession()
  // The history is on the Activity page, which is for managers.
  const canSeeAll = useCan('manager')
  if (!enabled) return null
  const label = count > 0 ? `Alerts, ${count} waiting` : 'Alerts'
  const callWait = waitedLabel(waits.call)
  // Your tables, other tables (their server hears them first), free tables (no server).
  type Group = 'mine' | 'others' | 'free'
  const groupOf = (id: string): Group => {
    const info = levels.get(id)
    if (!info || info.serverId === null) return 'free'
    return info.level === 'mine' ? 'mine' : 'others'
  }
  const quiet = (id: string) => levels.get(id)?.level === 'quiet'
  // Under a row of someone else's table: its server, and once everyone has been told, how long.
  const serverLine = (id: string) => {
    const info = levels.get(id)
    if (!info?.serverId || info.level === 'mine') return null
    const name = serverName(info.serverId) ?? 'Another server'
    return (
      <span className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted">
        <span className="inline-flex items-center gap-1.5">
          <ServerAvatar name={name} size="sm" />
          {name}
        </span>
        {info.level === 'all' && (
          <Badge tone="warning">
            <Clock className="mr-1 inline size-3 align-[-2px]" aria-hidden />
            Told everyone
          </Badge>
        )}
      </span>
    )
  }
  const groups: { key: Group; title: string; note?: string }[] = [
    { key: 'mine', title: 'Your tables' },
    {
      key: 'others',
      title: 'Other tables',
      note: `Their server hears these first. You hear one after it has waited ${callWait}.`,
    },
    { key: 'free', title: 'Free tables', note: 'No server yet: everyone hears these.' },
  ]
  return (
    <>
      <button
        type="button"
        aria-label={label}
        title={label}
        onClick={() => setOpen(true)}
        className={cx(
          'tap-target relative rounded-xl p-1.5 hover:bg-soft',
          count > 0 ? 'text-primary' : 'text-muted',
        )}
      >
        <Bell className="size-5" />
        <CountPill count={count} className="absolute -top-0.5 -right-0.5" />
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title="Alerts" size="md">
        <div className="space-y-4">
          {count === 0 && (
            <p className="rounded-xl bg-background p-4 text-center text-sm text-muted">
              Nothing waiting. Guests’ requests and tickets the kitchen marks ready show here.
            </p>
          )}
          {groups.map((group) => {
            const asks = requests.filter((g) => groupOf(g.request.id) === group.key)
            const food = ready.filter((t) => groupOf(t.ticket.id) === group.key)
            if (asks.length === 0 && food.length === 0) return null
            return (
              <section
                key={group.key}
                aria-label={group.title}
                className="overflow-hidden rounded-xl border border-border"
              >
                <h3 className="flex items-center gap-2 px-4 pt-3 pb-2 text-sm font-semibold">
                  <BellRing
                    className={cx('size-4', group.key === 'others' ? 'text-muted' : 'text-people')}
                    aria-hidden
                  />
                  {group.title}
                </h3>
                {group.note && <p className="-mt-1 px-4 pb-2.5 text-xs text-muted">{group.note}</p>}
                <GuestRequests
                  requests={asks}
                  settings={settings}
                  now={now}
                  grouped
                  quiet={(g) => quiet(g.request.id)}
                  decorate={(g) => serverLine(g.request.id)}
                />
                {food.length > 0 && (
                  <ul className="divide-y divide-border border-t border-border">
                    {food.map((t) => (
                      <li
                        key={t.ticket.id}
                        className={cx(
                          'flex items-center gap-3 px-4 py-3 text-sm',
                          quiet(t.ticket.id) && 'opacity-80',
                        )}
                      >
                        <div className="min-w-0 flex-1">
                          <ChefHat className="mr-1.5 inline size-4 align-[-3px] text-success" />
                          <Link
                            to={`/tables/${t.ticket.order_id}`}
                            className="font-semibold text-primary hover:underline"
                          >
                            #{t.ticket.kot_no} · {ticketWhere(t)}
                          </Link>
                          <span className="text-muted">
                            {' '}
                            · Food ready · {STATION_LABEL[t.ticket.station]}
                            {t.ticket.ready_at && ` · ${openFor(t.ticket.ready_at, now)} ago`}
                          </span>
                          {serverLine(t.ticket.id)}
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-label={`Dismiss ticket ${t.ticket.kot_no}`}
                          onClick={() => dismissReady(t.ticket.id)}
                        >
                          Dismiss
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )
          })}
          <Switch
            label="Sound"
            description="A short chime on this device when something new arrives."
            checked={sound}
            onChange={(e) => setSound(e.target.checked)}
          />
          <PushSwitch />
          <p className="text-xs text-muted">
            You hear your own tables at once, free tables at once, and any other table left waiting{' '}
            {callWait}.{' '}
            {platform.push?.publicKey
              ? 'Without the switch above, alerts show only while the app is open and online.'
              : 'Alerts show while the app is open and online on this device.'}
          </p>
          {canSeeAll && (
            <Link
              to="/activity?kind=alerts"
              className="block border-t border-border pt-3 text-center text-sm font-semibold text-primary hover:underline"
            >
              See all notifications →
            </Link>
          )}
        </div>
      </Modal>
    </>
  )
}
